// ============================================================
// Raya POS — marketing page content
// Every claim here describes something that ships today. Offline selling and
// payment-terminal integration are the only items allowed to say "coming soon";
// loyalty, promotions and direct printer / cash-drawer drivers are not built,
// so they are not mentioned as features.
// ============================================================

export const POS_PATH = '/point-of-sale';
export const POS_URL = `https://erp.rayasolutions.in${POS_PATH}`;

// ─── Hero counter mock ──────────────────────────────────────────────────────
// Prices are MRP-inclusive, the way an Indian counter rings them up.
export const HERO_LINES = [
  { name: 'Basmati rice 5 kg', meta: 'GST 5% · MRP ₹699', qty: 1, rate: 649 },
  { name: 'Sunflower oil 1 L', meta: 'Scanned as 3*', qty: 3, rate: 155, fresh: true },
  { name: 'Toor dal 1 kg', meta: 'GST 5% · MRP ₹175', qty: 2, rate: 168 },
  { name: 'Bath soap, pack of 4', meta: 'GST 18% · MRP ₹240', qty: 1, rate: 220 },
];
export const HERO_TOTAL = HERO_LINES.reduce((sum, l) => sum + l.qty * l.rate, 0); // 1,670
export const HERO_TENDER = [
  { mode: 'UPI', icon: 'qr-code', amount: 1500, note: 'QR paid' },
  { mode: 'Cash', icon: 'banknote', amount: 200, note: 'Change ₹30' },
];

// ─── Two ways to bill ───────────────────────────────────────────────────────
export const SCAN_ROWS = [
  { name: 'Paracetamol 650 mg, strip of 15', meta: 'Batch P2207 · exp 03/27 · FEFO', qty: 2, amount: '₹64.00' },
  { name: 'ORS sachet, orange', meta: 'Batch R118 · exp 11/26 · FEFO', qty: 4, amount: '₹84.00' },
  { name: 'Hand sanitiser 500 ml', meta: 'MRP ₹250 · sold at ₹235', qty: 3, amount: '₹705.00', fresh: true },
];

export const TILE_CATEGORIES = ['Coffee', 'Bakes', 'Sandwiches', 'Cold drinks'];
export const TILES = [
  { name: 'Filter coffee', price: '₹60', icon: 'coffee', tint: '#fef3c7', color: '#b45309' },
  { name: 'Cappuccino', price: '₹140', icon: 'coffee', tint: '#ffedd5', color: '#c2410c' },
  { name: 'Butter croissant', price: '₹110', icon: 'croissant', tint: '#fef9c3', color: '#a16207' },
  { name: 'Choco cake slice', price: '₹150', icon: 'cake-slice', tint: '#fce7f3', color: '#be185d' },
  { name: 'Veg grilled sandwich', price: '₹160', icon: 'sandwich', tint: '#dcfce7', color: '#15803d' },
  { name: 'Cold coffee', price: '₹170', icon: 'cup-soda', tint: '#e0f2fe', color: '#0369a1' },
];

export const BILLING_MODES = [
  {
    key: 'scan',
    icon: 'scan-line',
    label: 'Scan-first list',
    title: 'For grocery, pharmacy and hardware',
    blurb: 'A barcode scanner and a keyboard do all the work. Type 3* before a scan to ring up three at once, or start typing a name and the search answers instantly from the counter itself.',
    points: ['Barcode scanner + keyboard, no mouse needed', '3* quantity prefix for multiples', 'Instant local search by name or code'],
  },
  {
    key: 'tiles',
    icon: 'layout-grid',
    label: 'Touch tiles',
    title: 'For cafés, bakeries and kiosks',
    blurb: 'A category rail down the side and product tiles with pictures. Tap a tile to add it, tap again for another — built for a tablet on the counter.',
    points: ['Category rail for quick switching', 'Product tiles with pictures', 'Tap to add, made for touch screens'],
  },
];

// ─── Feature grid ───────────────────────────────────────────────────────────
export const POS_FEATURES = [
  { icon: 'lock', title: 'PIN sign-in for cashiers', desc: 'Counter devices are paired to the store once. Cashiers sign in with a PIN — no email or password at the till.' },
  { icon: 'user-check', title: 'Supervisor approvals', desc: 'Discounts above a cashier’s limit, voids and refunds wait for a supervisor to approve them on the spot.' },
  { icon: 'smartphone', title: 'Walk-in or by phone number', desc: 'Bill a walk-in, or find the customer by phone. Enter a GSTIN and the bill becomes a GST tax invoice automatically.' },
  { icon: 'shield-check', title: 'MRP guard', desc: 'The counter will not let an item go out above its MRP, so a wrong rate never reaches a customer.' },
  { icon: 'credit-card', title: 'Split payments', desc: 'Cash, UPI by QR, card or credit on account — mix them on one bill and the change is worked out for you.' },
  { icon: 'printer', title: 'Receipts three ways', desc: '80 mm or 58 mm thermal receipts, an A5 cash memo, or a receipt sent straight to the customer on WhatsApp.' },
  { icon: 'layers', title: 'Batches and serials', desc: 'Batches are picked first-expiry-first-out, and serial numbers are captured at the counter as the item is sold.' },
  { icon: 'pause-circle', title: 'Hold and recall', desc: 'Park a half-done bill while the customer fetches one more thing, serve the next person, then pick it back up.' },
  { icon: 'undo-2', title: 'Returns and exchanges', desc: 'Take an item back against its bill and refund in cash or as a credit note. Stock and books reverse together.' },
];

export const COMING_SOON = ['Offline selling', 'Payment-terminal integration'];

// ─── Connected to your books ────────────────────────────────────────────────
// Illustrative bill: ₹1,180 = ₹1,000 taxable + 9% CGST + 9% SGST, paid
// ₹1,000 by UPI and ₹180 cash, with stock that cost ₹720.
export const LEDGER_GROUPS = [
  {
    label: 'Sales',
    rows: [
      ['Customer — walk-in', '1,180.00', ''],
      ['Sales — Store 1', '', '1,000.00'],
      ['Output CGST 9%', '', '90.00'],
      ['Output SGST 9%', '', '90.00'],
    ],
  },
  {
    label: 'Cost of goods sold',
    rows: [
      ['Cost of goods sold', '720.00', ''],
      ['Stock in hand — Store 1', '', '720.00'],
    ],
  },
  {
    label: 'Receipt',
    rows: [
      ['UPI collections', '1,000.00', ''],
      ['Cash — Store 1, Counter 2', '180.00', ''],
      ['Customer — walk-in', '', '1,180.00'],
    ],
  },
];

export const BOOKS_POINTS = [
  { icon: 'receipt-indian-rupee', title: 'Every bill posts itself', desc: 'Sales, cost of goods sold and the receipt reach the ledger the instant the bill is saved — no day-end import.' },
  { icon: 'wallet', title: 'A cash ledger per store', desc: 'Each store keeps its own cash book, so the drawer and the books can be checked against each other.' },
  { icon: 'file-text', title: 'GSTR-1 ready', desc: 'Counter sales land as B2CS per tax rate, B2B invoices where a GSTIN was given, and credit notes for returns.' },
  { icon: 'timer', title: 'Shifts that close cleanly', desc: 'Opening float, cash in and out, a drawer count and a Z-report at the end of every shift.' },
];

export const MANAGER_POINTS = [
  'Live sales by store, terminal, cashier and hour',
  'Every bill, shift and cash movement in one place',
  'Reports with CSV export',
];

// ─── Set up in minutes ──────────────────────────────────────────────────────
export const SETUP_STEPS = [
  { num: '1', icon: 'toggle-right', title: 'Switch it on', desc: 'POS is off until you need it. Turn it on from Settings › General in Raya ERP.' },
  { num: '2', icon: 'store', title: 'Add a store and counter', desc: 'Name the store, add its counters and pick scan-first or touch tiles for each.' },
  { num: '3', icon: 'monitor-smartphone', title: 'Pair the device', desc: 'Open POS in the browser on the counter PC, tablet or iPad and pair it to that counter.' },
  { num: '4', icon: 'key-round', title: 'Cashiers sign in with a PIN', desc: 'Give each cashier a PIN. They are billing in seconds, with no email account needed.' },
];

// ─── Industries ─────────────────────────────────────────────────────────────
export const POS_INDUSTRIES = [
  { icon: 'shopping-basket', name: 'Grocery & supermarkets', desc: 'Scan-first billing, 3* for multiples and UPI at the till.' },
  { icon: 'pill', name: 'Pharmacy', desc: 'Batches picked by earliest expiry, with an MRP guard on every line.' },
  { icon: 'shirt', name: 'Apparel & footwear', desc: 'Exchanges and returns with a credit note, against stock across stores.' },
  { icon: 'smartphone', name: 'Electronics & mobile', desc: 'Serial numbers at the counter and GST tax invoices for business buyers.' },
  { icon: 'coffee', name: 'Cafés & bakeries', desc: 'Touch tiles with pictures, and held bills for the table that is still ordering.' },
  { icon: 'hammer', name: 'Hardware', desc: 'Fast search by name or code, and credit on account for regular contractors.' },
];

// ─── FAQ ────────────────────────────────────────────────────────────────────
export const POS_FAQS = [
  {
    q: 'Which devices does Raya POS run on?',
    a: 'Windows or Mac PCs, Android tablets and iPads, in the browser — nothing to install from a store. It can also be installed as a web app so it opens full screen from the home screen or taskbar. The scan-first layout works with a barcode scanner and keyboard; touch tiles are made for tablets.',
  },
  {
    q: 'Do cashiers need email accounts?',
    a: 'No. A counter device is paired to its store once, and cashiers sign in on it with a PIN. Discounts above a cashier’s limit, voids and refunds go to a supervisor for approval.',
  },
  {
    q: 'Does it work without internet?',
    a: 'Not yet. Raya POS needs a connection today, which is what lets every bill post to stock and the ledger the moment it is saved. Offline selling is coming soon.',
  },
  {
    q: 'Which printers can I use?',
    a: 'Receipts are laid out for 80 mm and 58 mm thermal rolls and as an A5 cash memo, and print from the browser to a printer set up on that counter device. There are no special printer or cash-drawer drivers to install. You can also send the receipt to the customer on WhatsApp.',
  },
  {
    q: 'How is GST handled at the counter?',
    a: 'Each item carries its GST rate and MRP from Raya ERP. A walk-in sale is billed as a retail sale; enter the buyer’s GSTIN and it becomes a GST tax invoice automatically. Counter sales flow into GSTR-1 as B2CS per rate, B2B invoices and credit notes.',
  },
  {
    q: 'What does Raya POS cost?',
    a: 'Raya POS runs on Raya ERP and is switched on from your own Settings, so you start with a Raya ERP subscription — the plans are on our pricing page. Tell us how many stores and counters you run and we will walk you through what applies to you.',
  },
];
